import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Table, FormGroup, Label, Input, Alert, Row, Col } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useAllBoards } from './hooks/useAllBoards';
import { QuickView } from './QuickView';
import { useShortenPcbSn } from './hooks/useShortenPcbSn';

export function XYPlotSelect({
    isOpen,
    toggle,
    targetRoute = "xyplot",
    onContinue,
    selectedCompanyId = null   // ← NEW: Company filter prop
}) {
    const history = useHistory();
    const { allBoards: basicBoards, getAllBoards } = useAllBoards();
    const { shortenPcbSn } = useShortenPcbSn();

    const [boardsWithData, setBoardsWithData] = useState([]);
    const [selectedBoards, setSelectedBoards] = useState([]);
    const [availableDataKeys, setAvailableDataKeys] = useState([]);

    // Support up to 4 X-Y pairs
    const [pairs, setPairs] = useState([
        { x: '', y: '' },
        { x: '', y: '' },
        { x: '', y: '' },
        { x: '', y: '' }
    ]);

    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(false);

    // Load boards when modal opens
    useEffect(() => {
        if (isOpen) {
            setBoardsWithData([]);
            setSelectedBoards([]);
            setPairs([
                { x: '', y: '' },
                { x: '', y: '' },
                { x: '', y: '' },
                { x: '', y: '' }
            ]);
            setAvailableDataKeys([]);
            setLoading(true);
            getAllBoards(`api/boards/`);
        } else {
            setBoardsWithData([]);
            setSelectedBoards([]);
            setPairs([
                { x: '', y: '' },
                { x: '', y: '' },
                { x: '', y: '' },
                { x: '', y: '' }
            ]);
        }
    }, [isOpen]);

    // Fetch latest data and extract numeric fields — with company filter
    useEffect(() => {
        if (!loading || basicBoards.length === 0) return;

        const fetchLatestDataForAll = async () => {
            setDataLoading(true);
            try {
                // === KEY CHANGE: Apply company filter if provided ===
                let boardsToProcess = basicBoards;
                if (selectedCompanyId) {
                    boardsToProcess = basicBoards.filter(board =>
                        String(board.companyId) === String(selectedCompanyId)
                    );
                }

                const enriched = await Promise.all(
                    boardsToProcess.map(async (basicBoard) => {
                        try {
                            const resp = await fetch(`api/boardData/pcbSn/${basicBoard.pcbSn}/1`, {
                                method: "GET",
                                headers: {
                                    "Authorization": `Bearer ${localStorage.getItem("token")}`
                                }
                            });

                            if (resp.ok) {
                                const dataArray = await resp.json();
                                if (dataArray.length > 0) {
                                    const latestData = dataArray[0];
                                    return { ...basicBoard, ...latestData };
                                }
                            }
                            return null;
                        } catch (err) {
                            console.warn(`Failed to fetch data for ${basicBoard.pcbSn}`, err);
                            return null;
                        }
                    })
                );

                const validBoards = enriched.filter(board => board !== null);
                setBoardsWithData(validBoards);

                const numericKeys = new Set();
                validBoards.forEach(board => {
                    if (board.dataRev === "1.1" && board.dataList) {
                        Object.entries(board.dataList).forEach(([key, value]) => {
                            if (value !== null && value !== undefined && value !== "") {
                                const num = Number(value);
                                if (!isNaN(num) && isFinite(num)) {
                                    numericKeys.add(key);
                                }
                            }
                        });
                    }
                });

                setAvailableDataKeys(Array.from(numericKeys).sort());

            } catch (err) {
                console.error("Error enriching boards with latest data", err);
            } finally {
                setLoading(false);
                setDataLoading(false);
            }
        };

        fetchLatestDataForAll();
    }, [basicBoards, loading, selectedCompanyId]);   // ← selectedCompanyId added as dependency

    const handleBoardSelect = (board, e) => {
        if (e.target.checked) {
            setSelectedBoards(prev => [...prev, board]);
        } else {
            setSelectedBoards(prev => prev.filter(b => b.id !== board.id));
        }
    };

    const handleSelectAllBoards = (e) => {
        if (e.target.checked) {
            setSelectedBoards([...boardsWithData]);
        } else {
            setSelectedBoards([]);
        }
    };

    const updatePair = (index, field, value) => {
        const newPairs = [...pairs];
        newPairs[index][field] = value;
        setPairs(newPairs);
    };

    const hasValidPairs = () => {
        return pairs.some(pair => pair.x && pair.y && pair.x !== pair.y);
    };

    const handleContinue = () => {
        if (selectedBoards.length === 0) {
            alert("Please select at least one board.");
            return;
        }

        const validPairs = pairs.filter(p => p.x && p.y && p.x !== p.y);

        if (validPairs.length === 0) {
            alert("Please define at least one valid X-Y pair (X and Y must be different).");
            return;
        }

        if (onContinue) {
            // Used by Romanak Plot
            onContinue(selectedBoards, validPairs);
        } else {
            // Normal X-Y Plot
            history.push(`/${targetRoute}`, {
                selectedBoards,
                pairs: validPairs
            });
        }

        toggle(); // Close modal
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static" centered>
            <ModalHeader toggle={toggle}>
                {targetRoute === "romanakplot" ? "Romanak Plot" : "X-Y Plot"} —
                Select Boards &amp; Multiple X-Y Pairs (dataRev 1.1)
                {selectedCompanyId && <span className="text-muted ms-3 small">(Company Filtered)</span>}
            </ModalHeader>
            <ModalBody>
                {loading || dataLoading ? (
                    <Alert color="info">
                        {loading ? "Loading board list..." : "Fetching latest data for boards..."}
                    </Alert>
                ) : (
                    <>
                        <Alert color="info">
                            Select boards, then define up to 4 X-Y pairs.
                            Each pair must have different X and Y fields.
                            {selectedCompanyId && " Only boards from the currently selected company are shown."}
                        </Alert>

                        {/* Boards Selection Table */}
                        <div className="mb-4">
                            <h5>
                                Select Boards ({boardsWithData.length} total with data)
                                {selectedCompanyId && " — Company Filtered"}
                            </h5>
                            <Table striped responsive size="sm">
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px', textAlign: 'center' }}>
                                            <Label check className="mb-0">
                                                <Input
                                                    type="checkbox"
                                                    onChange={handleSelectAllBoards}
                                                    checked={selectedBoards.length === boardsWithData.length && boardsWithData.length > 0}
                                                />
                                            </Label>
                                        </th>
                                        <th>Pcb SN</th>
                                        <th>Pcb MN</th>
                                        <th>Group</th>
                                        <th>Hardware Timestamp</th>
                                        <th style={{ width: '120px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {boardsWithData.map(board => (
                                        <tr key={board.id}>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                <Label check className="mb-0">
                                                    <Input
                                                        type="checkbox"
                                                        checked={selectedBoards.some(b => b.id === board.id)}
                                                        onChange={(e) => handleBoardSelect(board, e)}
                                                    />
                                                </Label>
                                            </td>
                                            <td><span className="code">{shortenPcbSn(board.pcbSn)}</span></td>
                                            <td>{board.pcbMn}</td>
                                            <td>{board.group || '-'}</td>
                                            <td>{board.timeSent || '—'}</td>
                                            <td>
                                                <QuickView board={board} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* X-Y Pairs Section */}
                        <h5 className="mb-3">Define X-Y Pairs (up to 4)</h5>
                        {pairs.map((pair, index) => (
                            <Row key={index} className="mb-3 align-items-center">
                                <Col md={1} className="text-end">
                                    <strong>Pair {index + 1}:</strong>
                                </Col>
                                <Col md={5}>
                                    <Input
                                        type="select"
                                        value={pair.x}
                                        onChange={e => updatePair(index, 'x', e.target.value)}
                                        disabled={availableDataKeys.length === 0}
                                    >
                                        <option value="">— Select X field —</option>
                                        {availableDataKeys.map(key => (
                                            <option key={key} value={key}>{key}</option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md={1} className="text-center">
                                    <strong>:</strong>
                                </Col>
                                <Col md={5}>
                                    <Input
                                        type="select"
                                        value={pair.y}
                                        onChange={e => updatePair(index, 'y', e.target.value)}
                                        disabled={availableDataKeys.length === 0}
                                    >
                                        <option value="">— Select Y field —</option>
                                        {availableDataKeys.map(key => (
                                            <option key={key} value={key}>{key}</option>
                                        ))}
                                    </Input>
                                </Col>
                            </Row>
                        ))}

                        {pairs.some((p) => p.x && p.y && p.x === p.y) && (
                            <Alert color="warning" className="mt-2">
                                One or more pairs have the same X and Y field. Please choose different fields.
                            </Alert>
                        )}
                    </>
                )}
            </ModalBody>

            <ModalFooter>
                <Button color="secondary" onClick={toggle}>Cancel</Button>
                <Button
                    color="primary"
                    onClick={handleContinue}
                    disabled={
                        selectedBoards.length === 0 ||
                        !pairs.some(pair => pair.x && pair.y && pair.x !== pair.y)
                    }
                >
                    Continue to {targetRoute === "romanakplot" ? "Romanak Plot" : "X-Y Plot"}
                    ({selectedBoards.length} boards)
                </Button>
            </ModalFooter>
        </Modal>
    );
}